import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggerService } from '../logger/logger.service';
import { UrlService } from '../url/url.service';
import { catchError, map, Observable, throwError } from 'rxjs';
import { LogsType } from '../../../shared/constants/logs-type.enum';
import { mapToXPagination } from '../../models/common/pagination.model';
import { ClientDto, ClientParamsDto, PaginatedClientsDto } from '../../models/client/client.dto';
import { UpdateClientDto } from '../../models/client/update-client.dto';
import { CreateClientDto } from '../../models/client/create-client.dto';

/**
* ClientApiService is responsible for managing client-related API operations.
*
* @example
* ```typescript
* // Use this in Components and traditional Services
* constructor(private readonly clientApiService: ClientApiService)
*
* // Use this in Interceptors, Guards and Functions
  private readonly clientApiService = inject(ClientApiService);
* ```
*/
@Injectable({
  providedIn: 'root',
})
export class ClientApiService {
  private readonly className = 'ClientApiService';
  private functionName = '';

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService,
    private readonly urlService: UrlService
  ) {}

  /**
   * Gets the clients based on the provided parameters.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientApiService.getApiClients(paramsClient);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientApiService.getApiClients(paramsClient);
   * ```
   *
   * @param params - The parameters to filter and paginate the clients
   * @returns Observable<PaginatedClientsDto> - The clients and the pagination information
   */
  getApiClients(params: ClientParamsDto): Observable<PaginatedClientsDto> {
    this.functionName = 'getApiClients';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);
    const url = this.urlService.getClientsUrl();

    return this.http
      .get<ClientDto[]>(url, {
        params: {
          ...(params.PageNumber !== undefined && { PageNumber: params.PageNumber.toString() }),
          ...(params.PageSize !== undefined && { PageSize: params.PageSize.toString() }),
          ...(params.PartialName && { PartialName: params.PartialName }),
          ...(params.ExactName && { ExactName: params.ExactName }),
          ...(params.Email && { Email: params.Email }),
          ...(params.Company && { Company: params.Company }),
          ...(params.NIF && { NIF: params.NIF }),
          ...(params.Phone && { Phone: params.Phone }),
          ...(params.PartialAddress && { PartialAddress: params.PartialAddress }),
        },
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<ClientDto[]>) => {
          const clients = response.body ?? [];
          const paginationHeader = response.headers.get('x-pagination');
          const pagination = mapToXPagination(paginationHeader);

          return { clients, pagination };
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Retrieves a client by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientApiService.getApiClientById(clientId);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientApiService.getApiClientById(clientId);
   * ```
   *
   * @param clientId - The unique identifier of the client
   * @returns Observable<ClientDto> - The client details
   */
  getApiClientById(clientId: string): Observable<ClientDto> {
    this.functionName = 'getApiClientById';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getClientByIdUrl(clientId);

    return this.http.get<ClientDto>(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Creates a new client.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientApiService.createApiClient(client);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientApiService.createApiClient(client);
   * ```
   *
   * @param client - The data for the new client
   * @returns Observable<ClientDto> - The created client details
   */
  createApiClient(client: CreateClientDto): Observable<ClientDto> {
    this.functionName = 'createApiClient';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getClientsUrl();

    return this.http.post<ClientDto>(url, client).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Updates an existing client by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientApiService.updateApiClient(clientId, client);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientApiService.updateApiClient(clientId, client);
   * ```
   *
   * @param clientId - The unique identifier of the client
   * @param client - The updated data for the client
   * @returns Observable<void> - Indicates the operation result
   */
  updateApiClient(clientId: string, client: UpdateClientDto): Observable<void> {
    this.functionName = 'updateApiClient';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getClientByIdUrl(clientId);
    return this.http.put<void>(url, client).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Deletes a client by its unique identifier.
   *
   * @example
   * ```typescript
   * // Use this in Components and traditional Services
   * this.clientApiService.deleteApiClient(clientId);
   *
   * // Use this in Interceptors, Guards and Functions
   * clientApiService.deleteApiClient(clientId);
   * ```
   *
   * @param clientId - The unique identifier of the client
   * @returns Observable<void> - Indicates the operation result
   */
  deleteApiClient(clientId: string): Observable<void> {
    this.functionName = 'deleteApiClient';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const url = this.urlService.getClientByIdUrl(clientId);
    return this.http.delete<void>(url).pipe(
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
